import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { favouritesService } from 'src/app/favourites/favourites.service';
import { Observable } from 'rxjs';
import {
  countRequiredExtraGroups,
  OptionModalData,
  toRenderedExtraList,
} from 'src/app/shared/extras';
import { RecipeOptionModalService } from 'src/app/recipe-option-modal/recipe-option-modal.service';
import { popupService } from 'src/app/popup/popup.service';
import { DesignService } from 'src/app/design/design.service';
import { GetterService } from 'src/app/service/getter-service.service';
import { RequestService } from 'src/app/service/request.service';
import { Product } from 'src/Model/Product-Interfaces';
import { brandFlavor } from 'src/app/brand/brand-context';

@Injectable({
  providedIn: 'root',
})
export class ProductCardService {
  constructor(
    private _requestService: RequestService,
    private spinner: NgxSpinnerService,
    private optionModal: RecipeOptionModalService,
    private legacyPopup: popupService,
    private design: DesignService,
    private _local: GetterService,
    private favService: favouritesService,
    private router: Router,
    private favouriteService: favouritesService
  ) {}

  /**
   * The product's item popup: the options modal with TemplateData.NewDesign
   * on, the legacy extras popup (app-popup) when off.
   */
  private openOptions(data: OptionModalData) {
    if (this.design.isNew) {
      this.optionModal.open(data);
    } else {
      this.legacyPopup.popUpData.next(data);
    }
  }

  openUrl(url: string) {
    this.router.navigateByUrl(this._requestService.setUrl(url));
  }

  /** true when adding this product goes through the options popup (extras,
   *  menu builder or a details view) — such products can sit in the basket
   *  as several lines with different extras, so the tile must not offer a
   *  single +/− for them */
  hasOptions(product: Product): boolean {
    let extras: any = product?.ProductExtraObject;
    if (typeof extras === 'string') {
      try {
        extras = extras.trim() ? JSON.parse(extras) : null;
      } catch {
        extras = null;
      }
    }
    if (!extras) return false;
    if (extras.ShowProductsDetails || extras.MenuBuilder) return true;
    return toRenderedExtraList(extras).length > 0;
  }

  /** how many of this product are in the basket across all its lines */
  inBasket(product: Product): number {
    const cart = JSON.parse(this._local.get('cartProductItems') || '[]') || [];
    return cart
      .filter((c) => c.ProductId == product.ProductId)
      .reduce((n, c) => n + (+c.ProductQuantity || 0), 0);
  }

  getProductModal(product: Product) {
    let extras;
    if (
      product.ProductExtraObject &&
      typeof product.ProductExtraObject == 'string'
    ) {
      extras = product.ProductExtraObject
        ? product.ProductExtraObject.trim()
        : {};
    }

    if (extras && typeof extras == 'string') {
      extras = JSON.parse(extras);
    }

    if (extras?.ShowProductsDetails) {
      let popupdata: OptionModalData = {
        MenuBuilder: false,
        dineIn: false,
        eatIn: false,
        filteredProduct: product,
        filteredRecipe: null,
        renderedExtraList: [],
        openForNonExtras: false,
        renderExtralistChooseOnes: 0,
      };
      this.openOptions(popupdata);
      return;
    }

    // builds the groups (with MaxQuantity) the popup resolves selection criteria on by key
    let renderedExtraList: Array<any> = toRenderedExtraList(extras);
    let renderExtralistChooseOnes = countRequiredExtraGroups(renderedExtraList);

    let popupdata: OptionModalData = {
      MenuBuilder: extras?.MenuBuilder,
      dineIn: false,
      eatIn: false,
      filteredProduct: product,
      filteredRecipe: null,
      renderedExtraList: renderedExtraList,
      openForNonExtras: false,
      renderExtralistChooseOnes: renderExtralistChooseOnes,
    };
    this.openOptions(popupdata);
  }

  /**
   * One product (or event) by id, with the customer's favourite flag. Moved
   * from the removed products/products.service.ts (Oct 2026).
   */
  getProductById(productId: number): Observable<any> {
    const brandFlavour = brandFlavor();
    const customerId = this._local.get('CustomerId') || 0;
    return this._requestService.getRequest(
      `api/Product/getProductByProductId/${brandFlavour}/${productId}/${customerId}`
    );
  }

  /**
   * Opens the options modal for a product whose extras are not on the object
   * in hand (the product / event details page), fetching them first. Moved
   * from the removed ProductsService.getProductModal (Oct 2026).
   */
  openWithFetchedExtras(product: Product, quantity: number) {
    this.spinner.show();
    this._requestService
      .getRequest('api/ProductExtra/getProductExtras/' + product.ProductId)
      .subscribe({
        next: (res) => {
          this.spinner.hide();
          let renderedExtraList: any[] = [];
          let renderExtralistChooseOnes = 0;
          if (res['Info']['Status'] == 200) {
            renderedExtraList = toRenderedExtraList(res['Data']);
            renderExtralistChooseOnes =
              countRequiredExtraGroups(renderedExtraList);
          }
          const data: OptionModalData = {
            MenuBuilder: res['Data']?.MenuBuilder,
            eatIn: false,
            dineIn: false,
            filteredProduct: product,
            filteredRecipe: null,
            renderedExtraList,
            openForNonExtras: false,
            renderExtralistChooseOnes,
            quantity,
          };
          this.openOptions(data);
        },
        error: () => {
          this.spinner.hide();
          alert('Network Error! Please try again');
        },
      });
  }

  addRemoveFavs(product: Product, status: boolean) {
    product.IsFavourite = !product.IsFavourite;
    let body = {
      FK_CustomerId: this._local.get('CustomerId'),
      FK_RecipeId: null,
      FK_ProductId: product.ProductId,
      Status: status,
    };

    this._requestService
      .postRequest('api/CustomerFavourites/updateItem', JSON.stringify(body))
      .subscribe({
        next: (res) => {
          if (res['Info'].Status == 200 && status) {
            this.favService.addFavouriteToList(product);
            return;
          }
          if (res['Info'].Status == 200 && !status) {
            this.favService.removeFavouriteFromList(product);
            return;
          }
          if (res['Info'].Status !== 200) {
            product.IsFavourite = !product.IsFavourite;
          }
        },
        error: () => {
          product.IsFavourite = !product.IsFavourite;
        },
      });
  }

  quantityControl(item: Product, flag: string) {
    let cart = JSON.parse(this._local.get('cartProductItems'));

    let updateCart = () => {
      this._local.set('cartProductItems', JSON.stringify(cart));
      this._requestService.storageChange.next();
    };

    if (flag == '-' && item.ProductQuantity == 1) {
      cart?.forEach((element) => {
        if (element.ProductId == item.ProductId) {
          cart.splice(cart.indexOf(element), 1);
          item.ProductQuantity = 0;
        }
      });
      updateCart();
      return;
    }
    if (flag == '+') {
      cart?.forEach((element) => {
        if (element.ProductId == item.ProductId) {
          element.ProductQuantity += 1;
          element.ProductTotal =
            element.ProductQuantity * element.ProductOldPrice;
        }
      });
      item.ProductQuantity += 1;
      updateCart();
      return;
    }
    if (flag == '-') {
      cart?.forEach((element) => {
        if (element.ProductId == item.ProductId) {
          element.ProductQuantity -= 1;
          element.ProductTotal =
            element.ProductQuantity * element.ProductOldPrice;
        }
      });
      item.ProductQuantity -= 1;
      updateCart();
      return;
    }
  }

  RemoveFavourite(product: Product) {
    this.spinner.show();
    this.favouriteService.deleteFavourite(product).subscribe((res) => {
      this.spinner.hide();
    });
  }

  kgListChanged(product: Product) {
    let cart = JSON.parse(this._local.get('cartProductItems'));
    cart?.forEach((element) => {
      if (element.ProductId == product.ProductId) {
        element.ProductQuantity = product.ProductQuantity;
        element.ProductTotal =
          element.ProductQuantity * product.ProductOldPrice;
      }
    });
    this._local.set('cartProductItems', JSON.stringify(cart));
    this._requestService.storageChange.next();
  }
}
